import type { Edge } from "@xyflow/react";
import type { DipFlowNode, DipPipelineColumn, DipPipelineItem } from "~/features/dataImportProjects/types";

const NODE_WIDTH = 380;
const NARROW_NODE_WIDTH = 228;
const COLUMN_GAP = 100;
const HEADER_Y = 0;
const ROW_START_Y = 60;
const ROW_GAP = 116;
const ITEM_HEIGHT = 58;
const COMPACT_ITEM_HEIGHT = 58;
const GROUP_WIDTH = 404;
const GROUP_HEIGHT = 148;
const GROUP_CHILD_HEIGHT = ITEM_HEIGHT;
const GROUP_CHILD_X = 12;
const GROUP_CHILD_Y = 72;
const GROUP_CHILD_GAP = 72;
const GROUP_BOTTOM_PADDING = 16;
const GROUP_ROW_SPACING = 36;

const EDGE_TYPE = "default" as const;
const EDGE_STYLE = { stroke: "#8DA5C8", strokeWidth: 1.5 } as const;
const GROUP_STYLE = {
  width: GROUP_WIDTH,
} as const;

const COMPACT_NODE_KINDS = new Set<DipPipelineItem["nodeKind"]>(["evidenceDrafts", "changeRequests"]);

function itemHeight(item: DipPipelineItem): number {
  return COMPACT_NODE_KINDS.has(item.nodeKind) ? COMPACT_ITEM_HEIGHT : ITEM_HEIGHT;
}

function groupHeight(item: DipPipelineItem): number {
  const childCount = item.children?.length ?? 0;
  if (childCount === 0) return GROUP_HEIGHT;
  return Math.max(
    GROUP_HEIGHT,
    GROUP_CHILD_Y + (childCount - 1) * GROUP_CHILD_GAP + GROUP_CHILD_HEIGHT + GROUP_BOTTOM_PADDING,
  );
}

function columnWidth(column: DipPipelineColumn): number {
  if (column.layout === "narrow") return NARROW_NODE_WIDTH;
  return column.items.some((item) => item.renderAsGroup) ? GROUP_WIDTH : NODE_WIDTH;
}

function columnXPositions(columns: DipPipelineColumn[]): number[] {
  const positions: number[] = [];
  let x = 0;
  for (const column of columns) {
    positions.push(x);
    x += columnWidth(column) + COLUMN_GAP;
  }
  return positions;
}

/**
 * Build a pipeline flow: header nodes + item nodes + edges from each item's `to`.
 * Accepts a variable number of columns.
 */
export function buildPipelineFlow(columns: DipPipelineColumn[]): {
  nodes: DipFlowNode[];
  edges: Edge[];
} {
  const nodes: DipFlowNode[] = [];
  const edges: Edge[] = [];
  const usesGroupRows = columns.some((column) =>
    column.items.some((item) => item.renderAsGroup || item.alignToGroupChildIndex != null),
  );
  const maxGroupHeight = Math.max(
    GROUP_HEIGHT,
    ...columns.flatMap((column) => column.items.filter((item) => item.renderAsGroup).map(groupHeight)),
  );
  const rowGap = usesGroupRows ? maxGroupHeight + GROUP_ROW_SPACING : ROW_GAP;
  const colX = columnXPositions(columns);

  columns.forEach((column, colIndex) => {
    const x = colX[colIndex];
    nodes.push({
      id: `header-${column.titleKey}`,
      type: "dipStageHeader",
      position: { x, y: HEADER_Y },
      data: {
        titleKey: column.titleKey,
        width: columnWidth(column),
      },
      draggable: false,
      selectable: false,
    });
    let y = ROW_START_Y;
    column.items.forEach((item) => {
      if (item.renderAsGroup) {
        const height = groupHeight(item);
        nodes.push({
          id: item.id,
          type: "dipGroup",
          position: { x, y },
          data: {
            label: item.title,
            detailPath: item.detailPath,
            peek: item.peek,
            showHandles: item.children && item.children.length > 0 ? false : item.showHandles,
          },
          draggable: false,
          selectable: false,
          style: { ...GROUP_STYLE, height },
        });
        item.children?.forEach((child, childIndex) => {
          nodes.push({
            id: child.id,
            type: "dipItem",
            parentId: item.id,
            extent: "parent",
            position: { x: GROUP_CHILD_X, y: GROUP_CHILD_Y + childIndex * GROUP_CHILD_GAP },
            style: { height: itemHeight(child) },
            data: {
              count: child.count,
              countTone: child.countTone,
              squareTone: child.squareTone,
              detailPath: child.detailPath,
              peek: child.peek,
              showHandles: child.showHandles,
              nodeKind: child.nodeKind,
              nodeVariant: child.nodeVariant,
              isDeactivated: child.isDeactivated,
              isDisabled: child.isDisabled,
              title: child.title,
              subtitle: child.subtitle,
              rightStats: child.rightStats,
              badges: child.badges,
              pendingCorrections: child.pendingCorrections,
            },
            draggable: false,
            selectable: false,
          });
          const childTargets = child.to == null ? [] : Array.isArray(child.to) ? child.to : [child.to];
          for (const target of childTargets) {
            edges.push({
              id: `${child.id}->${target}`,
              source: child.id,
              target,
              type: EDGE_TYPE,
              style: EDGE_STYLE,
            });
          }
        });
        const groupTargets = item.to == null ? [] : Array.isArray(item.to) ? item.to : [item.to];
        for (const target of groupTargets) {
          edges.push({
            id: `${item.id}->${target}`,
            source: item.id,
            target,
            type: EDGE_TYPE,
            style: EDGE_STYLE,
          });
        }
        y += height + GROUP_ROW_SPACING;
        return;
      }

      const alignedY =
        item.alignToGroupChildIndex == null
          ? y
          : y +
            GROUP_CHILD_Y +
            item.alignToGroupChildIndex * GROUP_CHILD_GAP +
            GROUP_CHILD_HEIGHT / 2 -
            itemHeight(item) / 2;
      nodes.push({
        id: item.id,
        type: "dipItem",
        position: { x, y: alignedY },
        style: { height: itemHeight(item) },
        data: {
          count: item.count,
          countTone: item.countTone,
          squareTone: item.squareTone,
          detailPath: item.detailPath,
          peek: item.peek,
          showHandles: item.showHandles,
          nodeKind: item.nodeKind,
          nodeVariant: item.nodeVariant,
          isDeactivated: item.isDeactivated,
          isDisabled: item.isDisabled,
          title: item.title,
          subtitle: item.subtitle,
          rightStats: item.rightStats,
          badges: item.badges,
          pendingCorrections: item.pendingCorrections,
        },
        draggable: false,
        selectable: false,
      });
      const targets = item.to == null ? [] : Array.isArray(item.to) ? item.to : [item.to];
      for (const target of targets) {
        edges.push({
          id: `${item.id}->${target}`,
          source: item.id,
          target,
          type: EDGE_TYPE,
          style: EDGE_STYLE,
        });
      }
      y += rowGap;
    });
  });

  const nodeIds = new Set(nodes.map((node) => node.id));
  return { nodes, edges: edges.filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target)) };
}
